import {
  type Dispatch,
  type SetStateAction,
  createContext,
  useEffect,
  useState,
} from "react";
import { Outlet, useOutletContext } from "react-router-dom";

import { SpinnerLoader } from "components/loader/loader";
import { type KeystoneOfferEligibility } from "data/keystone-api-mapping";
import { useClaimsConstants } from "data/ss-data-hooks";
import { useMultiStepFormState } from "hooks/use-multi-step-form-state";
import { useHideSupportChat } from "utils";

import styles from "./ting-offer.module.scss";

export type TingContextState = {
  firstName?: string;
  lastName?: string;
  emailAddress?: string;
  phone?: string;
  mobile?: ("isMobile" | "isTextOk")[];
  terms?: string;
  termsAgreed?: boolean;
  offerId?: string;
  offeringId?: string;
} | null;

export const TingContext = createContext<{
  state: TingContextState;
  setState: Dispatch<SetStateAction<TingContextState>>;
}>({ state: null, setState: () => null });

export const TingChecker = () => {
  useHideSupportChat();
  const { setFields, state } =
    useMultiStepFormState<TingContextState>(TingContext);
  const { data: claimsConstants, isLoading } = useClaimsConstants();
  const policyHolderPhone = claimsConstants?.PolicyHolderDetails.phoneNumber;

  const eligibilityData = useOutletContext<KeystoneOfferEligibility>();

  useEffect(() => {
    const terms = eligibilityData?.additionalDetails.find(
      (detail) => detail.assetTypeCode === "PROVIDER_TERMS_OF_USE",
    );
    setFields({
      terms: terms?.assetTextContent,
    });
  }, [eligibilityData?.additionalDetails, setFields]);

  useEffect(() => {
    if (eligibilityData?.offerId) {
      setFields({ offerId: eligibilityData.offerId ?? "" });
    }
  }, [eligibilityData?.offerId, setFields]);

  useEffect(() => {
    if (eligibilityData?.offeringId) {
      setFields({ offeringId: eligibilityData.offeringId ?? "" });
    }
  }, [eligibilityData?.offeringId, setFields]);

  useEffect(() => {
    if (policyHolderPhone && !state?.phone) {
      setFields({ phone: policyHolderPhone ?? "" });
    }
  }, [policyHolderPhone, setFields, state?.phone]);

  if (isLoading) {
    return (
      <div className={styles.loaderWrapper}>
        <SpinnerLoader />
      </div>
    );
  }

  return <Outlet />;
};

export const TingFlow = () => {
  const [state, setState] = useState<TingContextState>(null);
  return (
    <TingContext.Provider value={{ state, setState }}>
      <TingChecker />
    </TingContext.Provider>
  );
};
